import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { injectIntl } from 'react-intl';
import Tabulator from 'tabulator-tables/dist/js/tabulator.js';
import { Box, Button, Typography, Paper, CircularProgress, useMediaQuery } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useTheme } from '@mui/material/styles';
import { isCategory, getPremiumPeriodValue, getFrequencyIntlId, getTermsIntlNew } from '../../../utils/policy';
import { TERMS_OPTIONS } from '../../../constants/policy';
import { formatNumber } from '../../../utils/formatNumber';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import 'tabulator-tables/dist/css/tabulator_bootstrap5.min.css';
import './PolicyCategoryTableNew.css';
import { getPolicyReview as getPolicyReviewApi } from '../../../store/policyReview/api';
import { createColumnConfig } from './columnConfig';
import { TABULATOR_CONFIG, emojis } from './constants';
import { createTransformPolicyData } from './dataUtils';
import {
  createDestroyTabulator,
  createPersistenceKeys,
  createRemoveLocalStoragePersistence,
  createRecordTableHeight
} from './tableUtils';

const currentVersion = '2025-08-29';
const toSortableFields = sorters => (sorters || []).map(({ field, dir }) => ({ field, dir }));

function PolicyCategoryTableNew({
  intl,
  template,
  category,
  currency,
  onEditPolicy,
  editPolicy,
  viewingClientId,
  getPolicyReview,
  version,

  selectBasic,
  selectProtection,
  selectPremium,

  selectSaving,
  selectLifeInsured,
  selectBeneficiary,
  selectRemark,
  selectAccident,
  selectTotalWithdrawals,
  selectLumpSum,
  selectCurrencyPerYear,

  selectTotalCoveragePremium,
  selectShowLacks,
  selectTwoRows,

  onAddPolicy
}) {
  const [hideResizeBar, setHideResizeBar] = useState(false);
  const [sorted, setSorted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [redraw, setRedraw] = useState(0);
  const [tableHeight, setTableHeight] = useState(null);
  const isCompanyCategory = isCategory(category._id, 'company');
  const isGeneralCategory = isCategory(category._id, 'general');
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const tableRef1 = useRef(null);
  const tabulatorRef1 = useRef(null);
  const tableRef2 = useRef(null);
  const tabulatorRef2 = useRef(null);
  const tableContainerRef = useRef(null);
  const sortersRef = useRef({ table1: [], table2: [] });

  // Tabulator keeps the cellClick closures from the last column build, which does not rerun when only the
  // filtered category changes, so the click handlers must read the latest props through a ref.
  const latestPropsRef = useRef();
  latestPropsRef.current = { category, onEditPolicy, onAddPolicy };
  const getCategory = useCallback(() => latestPropsRef.current.category, []);
  const handleEditPolicy = useCallback(policy => latestPropsRef.current.onEditPolicy(policy), []);
  const handleAddPolicy = useCallback((...args) => latestPropsRef.current.onAddPolicy(...args), []);

  const columnConfig = useMemo(
    () =>
      createColumnConfig({
        intl,
        currency,
        sorted,
        isCompanyCategory,
        isGeneralCategory,
        selectTwoRows,
        selectBasic,
        selectProtection,
        selectPremium,
        selectSaving,
        selectLifeInsured,
        selectBeneficiary,
        selectRemark,
        selectAccident,
        selectTotalWithdrawals,
        selectLumpSum,
        selectCurrencyPerYear,
        version,
        viewingClientId,
        onEditPolicy: handleEditPolicy,
        getCategory,
        template,
        onAddPolicy: handleAddPolicy
      }),
    [
      intl.locale,
      isCompanyCategory,
      isGeneralCategory,
      currency,
      selectBasic,
      selectProtection,
      selectPremium,
      selectSaving,
      selectLifeInsured,
      selectBeneficiary,
      selectRemark,
      selectAccident,
      selectTotalWithdrawals,
      selectLumpSum,
      selectCurrencyPerYear,
      selectTotalCoveragePremium,
      selectShowLacks,
      selectTwoRows,
      sorted,
      version,
      getCategory,
      handleEditPolicy,
      handleAddPolicy
    ]
  );

  const transformPolicyData = useMemo(
    () =>
      createTransformPolicyData({
        intl,
        currency,
        isCompanyCategory,
        selectShowLacks,
        selectTotalWithdrawals,
        selectCurrencyPerYear
      }),
    [intl, currency, isCompanyCategory, selectShowLacks, selectTotalWithdrawals, selectCurrencyPerYear]
  );

  const initializeTabulator = (id, ref, tabulatorInstanceRef, columns) => {
    if (!ref.current || !ref.current.isConnected || !ref.current.offsetParent) {
      return;
    }

    try {
      tabulatorInstanceRef.current = new Tabulator(ref.current, {
        ...TABULATOR_CONFIG.DEFAULT_OPTIONS,
        index: '_id',
        data: [],
        columns,
        initialSort: sortersRef.current[id === 2 ? 'table2' : 'table1'],
        height: false,
        virtualDom: true,
        virtualDomBuffer: 50,
        persistence: {
          columns: true
        },
        persistenceID: `policy-review-${category?.title}-table-${id}`,
        width: null,
        rowFormatter: row => {
          const data = row.getData();
          if (data.isTotalRow) {
            row.getElement().classList.add('total-amount-row');
          }
        }
      });

      tabulatorInstanceRef.current.on('columnResized', function(column) {
        const scrollContainer = ref.current?.querySelector('.tabulator-tableholder');
        if (scrollContainer) {
          const scrollLeft = scrollContainer.scrollLeft;
          tabulatorInstanceRef.current.redraw(true);
          setTimeout(() => {
            scrollContainer.scrollLeft = scrollLeft;
          }, 100);
        }
      });

      tabulatorInstanceRef.current.on('dataSorted', function(sorters) {
        if (!resetting) {
          sortersRef.current[id === 2 ? 'table2' : 'table1'] = toSortableFields(sorters);
          if (sorters && sorters.length > 0) {
            setSorted(true);
          } else {
            setSorted(false);
          }
        }
      });

      tabulatorInstanceRef.current.on('cellEdited', async function(cell) {
        const policy = cell.getRow().getData();

        let field = cell.getField();
        let value = cell.getValue();
        let skipUpdate = false;
        let fieldsToUpdate = {};

        if (field === 'lumpSumAmount' || field === 'premium') {
          value = value.replaceAll(',', '');
          if (isNaN(value) || isNaN(parseFloat(value))) {
            cell.getRow().update({ [field]: cell.getOldValue() });
            toast.info(IconToast('error', intl.formatMessage({ id: 'please-enter-valid-number' })), {
              className: 'new-toast'
            });
            return;
          }
        }

        if (field === 'saving') {
          field = 'feature';
          if (value === intl.formatMessage({ id: 'y' })) {
            value = 'Saving';
          } else if (value === intl.formatMessage({ id: 'n' })) {
            value = 'Non Saving';
          } else {
            value = 'No Idea';
          }
        } else if (field === 'protectionTerms' || field === 'paymentTerms') {
          for (let term of Object.values(TERMS_OPTIONS)) {
            if (value === getTermsIntlNew(intl, term)) {
              value = term;
            }
          }
        } else if (field === 'lifeInsuredSelected' || field === 'beneficialSelected') {
          let found = false;
          let fieldWithoutSelected = field.replace('Selected', '');
          if (value === intl.formatMessage({ id: 'Self' })) {
            found = true;
            value = 'Self';
            fieldsToUpdate = { ...fieldsToUpdate, [fieldWithoutSelected]: '' };
            cell.getRow().update({ [fieldWithoutSelected]: '' });
          } else if (value === intl.formatMessage({ id: 'Not Me' })) {
            found = true;
            value = 'Not Me';
            if (policy[fieldWithoutSelected]) {
              cell.getRow().update({ [field]: policy[fieldWithoutSelected] });
            } else {
              fieldsToUpdate = { ...fieldsToUpdate, [fieldWithoutSelected]: '' };
              cell.getRow().update({ [fieldWithoutSelected]: '' });
            }
          } else if (value === intl.formatMessage({ id: 'No Idea' })) {
            found = true;
            value = 'No Idea';
            fieldsToUpdate = { ...fieldsToUpdate, [fieldWithoutSelected]: '' };
            cell.getRow().update({ [fieldWithoutSelected]: '' });
          } else if (value === intl.formatMessage({ id: 'N/A' })) {
            found = true;
            value = '';
            fieldsToUpdate = { ...fieldsToUpdate, [fieldWithoutSelected]: '' };
            cell.getRow().update({ [field]: '', [fieldWithoutSelected]: '' });
          }

          if (!found) {
            skipUpdate = true;
            cell.getRow().update({ [field]: cell.getOldValue() });
          }
        } else if (field === 'premium') {
          if (value && !policy.frequencyTranslation) {
            if (policy.frequency) {
              cell.getRow().update({ frequencyTranslation: intl.formatMessage({ id: getFrequencyIntlId(policy) }) });
            } else {
              fieldsToUpdate = { ...fieldsToUpdate, frequency: '12' };
              cell.getRow().update({ frequencyTranslation: intl.formatMessage({ id: 'Per Year' }) });
            }
          }
        } else if (field === 'frequencyTranslation') {
          field = 'frequency';
          if (value === intl.formatMessage({ id: 'N/A' })) {
            fieldsToUpdate = { ...fieldsToUpdate, premium: 0, frequency: '12' };
            cell.getRow().update({ premium: '', frequency: '12', frequencyTranslation: '' });
            value = '12';
          } else if (value === intl.formatMessage({ id: 'Per Month' })) {
            value = '1';
          } else if (value === intl.formatMessage({ id: 'Per Quarter' })) {
            value = '3';
          } else if (value === intl.formatMessage({ id: 'Per Half Year' })) {
            value = '6';
          } else if (value === intl.formatMessage({ id: 'Per Year' })) {
            value = '12';
          }
          cell.getRow().update({ frequency: value });
        }

        if (field === 'premium' || field === 'frequency') {
          cell.getRow().update({
            premiumPerYear: policy.premium
              ? formatNumber(getPremiumPeriodValue(policy, 'Yearly', currency), intl) || ''
              : ''
          });
        }

        if (!skipUpdate) {
          recordTableHeight();
          const scrollContainer = ref.current?.querySelector('.tabulator-tableholder');
          if (scrollContainer) {
            var scrollLeft = scrollContainer.scrollLeft;
          }
          setDataLoading(true);

          editPolicy({ [field]: value, ...fieldsToUpdate, id: policy._id });

          setTimeout(async () => {
            if (viewingClientId) {
              getPolicyReview(viewingClientId);
              await getPolicyReviewApi(viewingClientId);

              setTimeout(async () => {
                const scrollContainer = ref.current?.querySelector('.tabulator-tableholder');
                if (scrollContainer) {
                  scrollContainer.scrollLeft = scrollLeft;
                }
                setDataLoading(false);
              }, 800);
            }
          }, 500);
        }
      });
    } catch (error) {
      console.error(`Failed to initialize Tabulator for ${id}:`, error);
    }
  };

  const destroyTabulator = useCallback(createDestroyTabulator(), []);

  const persistenceKeys = useMemo(() => createPersistenceKeys(category?.title), [category?.title]);

  const removeLocalStoragePersistence = useCallback(createRemoveLocalStoragePersistence(persistenceKeys), [
    persistenceKeys
  ]);

  const setupTabulators = (removeLocalStorage, preserveSorters = true) => {
    if (preserveSorters && tabulatorRef1.current) {
      sortersRef.current.table1 = toSortableFields(tabulatorRef1.current.getSorters());
    }
    if (preserveSorters && tabulatorRef2.current) {
      sortersRef.current.table2 = toSortableFields(tabulatorRef2.current.getSorters());
    }

    const tempPersistenceKeys = [];
    for (let key of persistenceKeys) {
      let value = localStorage.getItem(key);
      if (value && key.endsWith('-columns')) {
        tempPersistenceKeys.push({ key, value });
      }
    }

    persistenceKeys.filter(key => key.endsWith('-sort')).forEach(key => localStorage.removeItem(key));

    destroyTabulator(tabulatorRef1);
    destroyTabulator(tabulatorRef2);

    let previousLanguage = localStorage.getItem(`tabulator-policy-review-${category?.title}-table-language`);
    let previousCurrency = localStorage.getItem(`tabulator-policy-review-${category?.title}-table-currency`);
    let previousVersion = localStorage.getItem(`tabulator-policy-review-${category?.title}-table-version`);

    setTimeout(() => {
      if (
        removeLocalStorage ||
        (previousLanguage && intl.locale !== previousLanguage) ||
        (previousCurrency && currency !== previousCurrency) ||
        previousVersion !== currentVersion
      ) {
        removeLocalStoragePersistence();
      } else {
        tempPersistenceKeys.forEach(({ key, value }) => {
          localStorage.setItem(key, value);
        });
      }
    }, 200);

    setTimeout(() => {
      if (selectTwoRows) {
        initializeTabulator(1, tableRef1, tabulatorRef1, columnConfig.table1);
        initializeTabulator(2, tableRef2, tabulatorRef2, columnConfig.table2);
      } else {
        initializeTabulator(3, tableRef1, tabulatorRef1, columnConfig.singleTable);
      }
      localStorage.setItem(`tabulator-policy-review-${category?.title}-table-language`, intl.locale);
      localStorage.setItem(`tabulator-policy-review-${category?.title}-table-currency`, currency);
      localStorage.setItem(`tabulator-policy-review-${category?.title}-table-version`, currentVersion);
    }, 400);

    setTimeout(() => {
      destroyTabulator(tabulatorRef1);
      destroyTabulator(tabulatorRef2);
    }, 800);

    setTimeout(() => {
      if (selectTwoRows) {
        initializeTabulator(1, tableRef1, tabulatorRef1, columnConfig.table1);
        initializeTabulator(2, tableRef2, tabulatorRef2, columnConfig.table2);
      } else {
        initializeTabulator(3, tableRef1, tabulatorRef1, columnConfig.singleTable);
      }
      localStorage.setItem(`tabulator-policy-review-${category?.title}-table-language`, intl.locale);
      localStorage.setItem(`tabulator-policy-review-${category?.title}-table-currency`, currency);
    }, 1200);

    setTimeout(() => {
      setRedraw(redraw + 1);
    }, 1600);
  };

  const recordTableHeight = useCallback(createRecordTableHeight(tableContainerRef, setTableHeight), [
    tableContainerRef,
    setTableHeight
  ]);

  const handleAlignTables = () => {
    let basicInfoColumnCount = selectBasic
      ? 3 + (selectSaving || 0) + (selectLifeInsured || 0) + (selectBeneficiary || 0) + (selectRemark || 0)
      : 0;
    if (version === 'compact') {
      var protectionColumnCount = selectProtection ? (isGeneralCategory ? 2 + (selectTotalWithdrawals || 0) : 2) : 0;
    } else {
      var protectionColumnCount = selectProtection
        ? isGeneralCategory
          ? 4 + (selectTotalWithdrawals || 0)
          : 5 + (selectAccident || 0)
        : 0;
    }
    let premiumColumnCount = selectPremium ? 4 + (selectCurrencyPerYear || 0) + (selectLumpSum || 0) : 0;

    try {
      if (tabulatorRef1.current) {
        const columns = tabulatorRef1.current.getColumns();
        columns.forEach(column => {
          if (column.getField() !== 'subCategory' && column.getField() !== 'edit') {
            column.setWidth((intl.locale === 'en' ? 1225 : 1237) / (3 + basicInfoColumnCount));
          }
        });
        tabulatorRef1.current.redraw(true);
      }
      if (tabulatorRef2.current) {
        const columns = tabulatorRef2.current.getColumns();
        columns.forEach(column => {
          if (column.getField() !== 'subCategory' && column.getField() !== 'edit') {
            column.setWidth((intl.locale === 'en' ? 1200 : 1250) / (2 + protectionColumnCount + premiumColumnCount));
          }
        });
        tabulatorRef2.current.redraw(true);
      }
    } catch (error) {
      console.error('Error resetting table:', error);
    }
  };

  const handleResetTable = () => {
    recordTableHeight();
    setLoading(true);
    setResetting(true);
    sortersRef.current = { table1: [], table2: [] };
    setSorted(false);
    setupTabulators(true, false);

    setTimeout(() => {
      try {
        if (tabulatorRef1.current) {
          const columns = tabulatorRef1.current.getColumns();
          columns.forEach(column => {
            column.setWidth(null);
          });
          tabulatorRef1.current.redraw(true);
        }
        if (tabulatorRef2.current) {
          const columns = tabulatorRef2.current.getColumns();
          columns.forEach(column => {
            column.setWidth(null);
          });
          tabulatorRef2.current.redraw(true);
        }
      } catch (error) {
        console.error('Error resetting table:', error);
      } finally {
        setLoading(false);
        setResetting(false);
      }
    }, 1800);
  };

  useEffect(() => {
    const handleMouseMove = () => {
      if (!isXsScreen) {
        setHideResizeBar(true);
      }
    };
    window.addEventListener('mousemove', handleMouseMove, { once: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    try {
      let newTableData = transformPolicyData(category);
      let newTableDataWithoutTotal = [...newTableData];
      if (newTableDataWithoutTotal.length > 1) {
        newTableDataWithoutTotal.pop();
      }

      if (!tabulatorRef1.current?.initialized || (!tabulatorRef2.current?.initialized && selectTwoRows)) {
        setTimeout(() => {
          setRedraw(redraw + 1);
        }, 500);
      } else {
        let table1Data = !selectTotalCoveragePremium || selectTwoRows ? newTableDataWithoutTotal : newTableData;
        let table2Data = selectTotalCoveragePremium ? newTableData : newTableDataWithoutTotal;
        tabulatorRef1.current.setData(table1Data);
        if (selectTwoRows) {
          tabulatorRef2.current.setData(table2Data);
        }

        setTimeout(() => {
          if (
            !tabulatorRef1.current ||
            tabulatorRef1.current.getData().length !== table1Data.length ||
            (selectTwoRows && (!tabulatorRef2.current || tabulatorRef2.current.getData().length !== table2Data.length))
          ) {
            setRedraw(redraw + 1);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error setting table data:', error);
      setTimeout(() => {
        setRedraw(redraw + 1);
      }, 500);
    }
  }, [category, selectTotalCoveragePremium, selectTwoRows, redraw]);

  useEffect(() => {
    let noLocalStorage = true;
    for (let persistenceKey of persistenceKeys) {
      if (localStorage.getItem(persistenceKey)) {
        noLocalStorage = false;
        break;
      }
    }
    recordTableHeight();
    setLoading(true);
    setupTabulators(noLocalStorage);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, [columnConfig, persistenceKeys]);

  // iPad / WebKit (WKWebView) defensive re-measure.
  // On desktop Chrome the table renders fine, but inside the iOS app three issues
  // all trace back to WebKit not re-measuring the table after a visibility / layout
  // change: (1) rows go blank after an inline edit, because the table is hidden via
  // opacity:0 while Tabulator.setData() runs during the save and is never redrawn
  // once it becomes visible again; (2)/(3) column widths and the horizontal scroll
  // extent are wrong after the first paint or after an orientation change.
  // Forcing a redraw once the table is visible again (and on orientation change)
  // makes WebKit recompute row positions and column widths. redraw(true) is
  // idempotent, so this is a no-op on engines that already render correctly.
  useEffect(() => {
    const redrawTables = () => {
      try {
        tabulatorRef1.current?.redraw(true);
        if (selectTwoRows) {
          tabulatorRef2.current?.redraw(true);
        }
      } catch (e) {
        /* table not initialised yet */
      }
    };

    if (!loading && !dataLoading) {
      redrawTables();
    }

    window.addEventListener('orientationchange', redrawTables);
    return () => window.removeEventListener('orientationchange', redrawTables);
  }, [loading, dataLoading, selectTwoRows]);

  return (
    <Box>
      {!template && !hideResizeBar && (
        <style>{`
        .tabulator .tabulator-header .tabulator-col:not(.spacer) {
          border-right: 4px solid #dee2e6;
        }
      `}</style>
      )}
      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <Box
          className="table-title"
          style={{ width: intl.locale === 'en' ? 120 : 100, borderRadius: isXsScreen ? 4 : undefined }}
        >
          {emojis[category?.title]} {intl.formatMessage({ id: category?.title })}
        </Box>

        {!template && (
          <Box>
            {selectTwoRows && (
              <Button
                onClick={handleAlignTables}
                variant="outlined"
                color="primary"
                disabled={loading}
                style={{ marginRight: 6, minWidth: 'unset' }}
              >
                <FormatAlignCenterIcon />{' '}
                {!isXsScreen && (
                  <Typography style={{ marginLeft: 5, fontSize: 14 }}>
                    {intl.formatMessage({ id: 'evenly-distribute' })}
                  </Typography>
                )}
              </Button>
            )}
            {!isXsScreen && (
              <Button
                onClick={() => setHideResizeBar(!hideResizeBar)}
                variant="outlined"
                color="primary"
                disabled={loading}
                style={{ marginRight: 6, minWidth: 'unset' }}
              >
                <SyncAltIcon />{' '}
                <Typography style={{ marginLeft: 5, fontSize: 14 }}>
                  {hideResizeBar
                    ? intl.formatMessage({ id: 'resize-width' })
                    : intl.formatMessage({ id: 'finish-resizing' })}
                </Typography>
              </Button>
            )}
            <Button onClick={handleResetTable} variant="outlined" color="primary" disabled={loading}>
              <RefreshIcon />{' '}
              <Typography style={{ marginLeft: 5, fontSize: 14 }}>{intl.formatMessage({ id: 'reset' })}</Typography>
            </Button>
          </Box>
        )}
      </Box>
      <Box ref={tableContainerRef} style={{ height: dataLoading && tableHeight ? tableHeight : 'auto' }}>
        {(loading || dataLoading) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress color="primary" />
            <Typography color="primary" style={{ fontSize: 22, fontWeight: 500, marginLeft: 16 }}>
              {dataLoading ? intl.formatMessage({ id: 'saving-data' }) : intl.formatMessage({ id: 'loading' })}
            </Typography>
          </Box>
        )}

        <Box style={{ opacity: loading || dataLoading ? 0 : 1 }}>
          {selectTwoRows ? (
            <>
              <Paper
                elevation={6}
                style={{
                  marginBottom: '20px',
                  width: 'fit-content',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  color: 'rgba(0, 0, 0, 0.87)'
                }}
              >
                <div ref={tableRef1} />
              </Paper>
              <Paper
                elevation={6}
                style={{
                  width: 'fit-content',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  color: 'rgba(0, 0, 0, 0.87)'
                }}
              >
                <div ref={tableRef2} />
              </Paper>
            </>
          ) : (
            <Paper
              elevation={6}
              style={{
                width: 'fit-content',
                maxWidth: '100%',
                overflowX: 'auto',
                color: 'rgba(0, 0, 0, 0.87)'
              }}
            >
              <div ref={tableRef1} />
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default injectIntl(PolicyCategoryTableNew);
